import { Field, Form, Formik, FormikErrors } from 'formik';
import React from 'react';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import ROUTES from '@/lib/routes';
import Loading from '@/components/spinner/Loading';
import InputText from '@/components/inputs/Text';
import Button from '@/components/inputs/Button';
import useAlerts from '@/components/hooks/useAlert';
import { handleResponseError, isEmail } from '@/lib/utils';
import api from '@/lib/http/api';
import Link from 'next/link';
import InputTextArea from '@/components/inputs/TextArea';
import useOAuthUser from '../hooks/useOAuthUser';

interface Values {
  name: string;
  email: string;
  subject: string;
  message: string;
  recaptchaValue: string;
}

export default function ContactUsForm() {
  const router = useRouter();
  const { addAlert } = useAlerts();
  const { oAuthUser: user, loading, mutateOAuth: mutate } = useOAuthUser();
  const recaptchaRef = React.createRef<any>();

  if (loading) {
    return <Loading />;
  }

  const MAX_VALUE_TEXT = 100;
  const MAX_VALUE_MESSAGE = 2048;

  return (
    <Formik<Values>
      initialValues={{
        name: user && user.name ? user.name : '',
        email: user ? user.email : '',
        subject: '',
        message: '',
        recaptchaValue: '',
      }}
      validateOnChange={true}
      validateOnBlur={true}
      validate={(values) => {
        const errors: FormikErrors<Values> = {};
        if (!values.name) {
          errors.name = `provide a name`;
        }
        if (!values.email || !isEmail(values.email)) {
          errors.email = `provide a valid email`;
        }
        if (!values.subject) {
          errors.subject = `provide a subject`;
        }
        if (!values.message) {
          errors.message = `provide a message`;
        }
        if (values.message.length > MAX_VALUE_MESSAGE) {
          errors.message = `message is too long please go under ${MAX_VALUE_MESSAGE}`;
        }
        if (values.name.length > MAX_VALUE_TEXT) {
          errors.name = `name is too long please go under ${MAX_VALUE_TEXT}`;
        }
        if (values.subject.length > MAX_VALUE_TEXT) {
          errors.subject = `subject is too long please go under ${MAX_VALUE_TEXT}`;
        }
        return errors;
      }}
      onSubmit={async (values, { setSubmitting, setErrors, setValues }) => {
        const res = await recaptchaRef.current.executeAsync();
        if (!res) {
          addAlert({
            type: 'error',
            title: 'Challenge!',
            content: 'You should pass recaptcha challenge',
          });
          return;
        }
        try {
          values.recaptchaValue = res;
          await api.post('/contact_requests', values);
          addAlert({
            id: 'contact',
            title: 'Message sent!',
            content: 'Thanks for sending us a message we will contact you soon!',
            type: 'success',
          });
          router.replace(ROUTES.home);
        } catch (e) {
          handleResponseError(e, addAlert);
        }
        setSubmitting(false);
      }}>
      {({ isSubmitting, values }) => (
        <div>
          <Form>
            <div className="sm:flex sm:space-x-4">
              <div className="mt-1 ">
                <Field
                  id="name"
                  type="text"
                  name="name"
                  placeholder={'Name'}
                  component={InputText}
                  label="Name"
                />
              </div>
              <div className="mt-4 sm:mt-1 ">
                <Field
                  id="email"
                  type="email"
                  name="email"
                  placeholder={'Email'}
                  component={InputText}
                  label="Email"
                />
              </div>
            </div>
            <div>
              <div className="mt-4 ">
                <Field
                  id="subject"
                  type="text"
                  name="subject"
                  placeholder={'Subject'}
                  component={InputText}
                  label="Subject"
                />
              </div>
            </div>
            <div className="mt-4">
              <div className="mt-1 ">
                <Field
                  id="message"
                  rows={5}
                  className="h-40"
                  name="message"
                  placeholder={'Message'}
                  component={InputTextArea}
                  label="Message"
                />
              </div>
            </div>

            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
              onChange={(captchaCode) => {
                if (!captchaCode) {
                  return;
                }
                recaptchaRef.current.reset();
              }}
            />

            <div className="mt-10 flex justify-end items-center space-x-4">
              <Link href={ROUTES.home}>
                <a>Cancel</a>
              </Link>
              <Button type="submit" loading={isSubmitting} outline dark size={'large'}>
                Send
              </Button>
            </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}
